import { Link } from 'react-router-dom';
import type { BenchmarkPayload, SessionSummary } from '@titrate/shared';
import { useBenchmarks, useSessions } from '../lib/queries';
import { EmptyState, Spinner } from '../components/ui';

/**
 * Where a row leads, and what to call it. Derived once so the table and the
 * mobile list can never disagree about which action a session offers.
 */
function rowAction(session: SessionSummary, bench: BenchmarkPayload | undefined) {
  if (!session.completedAt) return { to: `/exam/${session.id}`, label: 'Resume' };
  if (bench) return { to: `/results/${bench.id}`, label: 'Results' };
  return { to: `/review/${session.id}`, label: 'Review' };
}

function accuracyLabel(session: SessionSummary, bench: BenchmarkPayload | undefined) {
  if (bench) return `${bench.accuracyScore}%`;
  return session.completedAt ? '—' : 'In progress';
}

export function History() {
  const { data: sessions, isLoading } = useSessions();
  const { data: benchmarks } = useBenchmarks();

  if (isLoading) return <Spinner />;

  const benchmarkBySession = new Map((benchmarks?.benchmarks ?? []).map((b) => [b.sessionId, b]));
  const rows = sessions?.sessions ?? [];

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-3xl font-bold tracking-tight text-ink">Session history</h1>
        <p className="mt-2 text-muted">Every baseline and practice set you have sat.</p>
      </header>

      {rows.length === 0 ? (
        <EmptyState
          title="Nothing here yet"
          body="Your sessions will appear here once you have sat your first assessment."
          action={<Link to="/dashboard" className="btn-primary">Go to my dashboard</Link>}
        />
      ) : (
        <>
          {/*
            Phones get a list rather than a table. Six columns do not fit in
            343px, and the previous `overflow-hidden` wrapper meant they were
            not merely cramped but clipped — the accuracy figure and the
            Resume/Results link, which is the only reason to visit this page,
            were both unreachable below roughly 640px.
          */}
          <ul className="card divide-y divide-line sm:hidden">
            {rows.map((s) => {
              const bench = benchmarkBySession.get(s.id);
              const action = rowAction(s, bench);
              return (
                <li key={s.id} className="p-4">
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="font-semibold text-ink">{s.examCode}</span>
                    <span className="text-sm tabular-nums font-semibold text-ink">
                      {accuracyLabel(s, bench)}
                    </span>
                  </div>
                  <p className="mt-1 text-sm capitalize text-muted">
                    {s.mode} ·{' '}
                    <span className="normal-case">
                      {new Date(s.startedAt).toLocaleDateString('en-AU', { dateStyle: 'medium' })}
                    </span>
                  </p>
                  <p className="mt-0.5 text-sm tabular-nums text-muted">
                    {s.answeredCount}/{s.questionCount} answered
                  </p>
                  <Link to={action.to} className="btn-secondary mt-3 w-full">
                    {action.label}
                  </Link>
                </li>
              );
            })}
          </ul>

          {/* `overflow-x-auto` + a floor width: narrow tablets scroll rather than clip. */}
          <div className="card hidden overflow-x-auto sm:block">
            <table className="w-full min-w-[640px] text-sm">
              <thead className="border-b border-line bg-raised text-left">
                <tr>
                  <th className="px-4 py-3 font-semibold text-muted">Date</th>
                  <th className="px-4 py-3 font-semibold text-muted">Exam</th>
                  <th className="px-4 py-3 font-semibold text-muted">Type</th>
                  <th className="px-4 py-3 font-semibold text-muted">Answered</th>
                  <th className="px-4 py-3 font-semibold text-muted">Accuracy</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {rows.map((s) => {
                  const bench = benchmarkBySession.get(s.id);
                  const action = rowAction(s, bench);
                  return (
                    <tr key={s.id} className="border-b border-line last:border-0">
                      <td className="px-4 py-3 text-muted">
                        {new Date(s.startedAt).toLocaleDateString('en-AU', { dateStyle: 'medium' })}
                      </td>
                      <td className="px-4 py-3 font-medium text-ink">{s.examCode}</td>
                      <td className="px-4 py-3 capitalize text-muted">{s.mode}</td>
                      <td className="px-4 py-3 tabular-nums text-muted">
                        {s.answeredCount}/{s.questionCount}
                      </td>
                      <td className="px-4 py-3 tabular-nums font-semibold text-ink">
                        {accuracyLabel(s, bench)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Link to={action.to} className="font-medium text-brand-700 dark:text-brand-300 hover:underline">
                          {action.label}
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
